import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { ArrowRightSmall, ChatFill, LikeFill, Xmark } from '@peacecover/icon';
import { useGetSceneSubscriptions } from '../../../domains/scene/ui/hooks/useGetSceneSubscriptions';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import EmptyCase from '../../../components/errors/EmptyCase';
import { ReactComponent as GhostProfileIcon } from '../../../icons/thumbnail.ghost.profile.svg';
import type { components } from '../../../types/schema';

type SceneSubscription = components['schemas']['ApiV1UserSceneSubscription'];

const PreviewSubscriptionList = () => {
  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useGetSceneSubscriptions();
  const previewList = data?.pages?.flatMap((page) => page.userSceneSubscriptions ?? []);

  const loadMore = () => {
    if (isFetching || !hasNextPage) return;
    fetchNextPage();
  };

  if (isLoading) {
    return null;
  }

  if (data?.pages?.length && previewList?.length === 0) {
    return (
      // @ts-ignore
      <EmptyCase emoji="😳" title="보관함이 비어있어요" />
    );
  }

  return (
    <div className="h-full">
      <Virtuoso
        overscan={480}
        defaultItemHeight={260}
        style={{ height: '100%' }}
        data={previewList}
        endReached={loadMore}
        atBottomThreshold={200}
        itemContent={(_index, subscription: SceneSubscription) => (
          <PreviewSubscriptionItem subscription={subscription} />
        )}
      />
    </div>
  );
};

export default PreviewSubscriptionList;

const PreviewSubscriptionItem = ({ subscription }: { subscription: SceneSubscription }) => {
  const navigate = useNavigate();
  return (
    <div className="pt-[20px]">
      <div className="px-[27px] flex items-center gap-[7px]">
        <div className="flex items-center gap-[4px]">
          <ProfileAvatar
            thumbnailUrl={subscription?.castRelationship?.personAThumbnailUrl ?? ''}
            widthAndHeight="27px"
          />
          <p className="text-[12px] font-medium leading-[14px] text-[#FFFFFF]">
            {subscription?.castRelationship?.personAName}
          </p>
        </div>
        <Xmark size={12} className="text-main-light-3" />
        <div className="flex items-center gap-[4px]">
          <ProfileAvatar
            thumbnailUrl={subscription?.castRelationship?.personBThumbnailUrl ?? ''}
            widthAndHeight="27px"
          />
          <p className="text-[12px] font-medium leading-[14px] text-[#FFFFFF]">
            {subscription?.castRelationship?.personBName}
          </p>
        </div>
      </div>
      <div className="px-[22px] py-[15px] flex items-start gap-[10px]">
        <GhostProfileIcon width={26} height={26} className="flex-shrink-0" />
        <div className="flex flex-col items-start gap-[5px]">
          <p className="text-separator-light-1 text-[14px] font-semibold leading-[17px]">
            {subscription?.lastReadContent?.speaker}
          </p>
          <p className="text-pure-white text-[15px] leading-[19px]">
            {subscription?.lastReadContent?.text}
          </p>
        </div>
      </div>
      <div className="border-t-[1px] border-b-[5px] border-[#171717] flex items-center justify-between px-[22px] py-[14px]">
        <div className="flex items-center gap-[12px]">
          <div className="flex items-center gap-[4px]">
            <LikeFill size={18} colors={['#FF45A9', '#FF6A6A']} />
            <p className="text-[11px] font-semibold leading-[13px] text-pure-white">999</p>
          </div>
          <div className="flex items-center gap-[4px] text-pure-white">
            <ChatFill size={18} />
            <p className="text-[11px] font-semibold leading-[13px]">999</p>
          </div>
        </div>
        <button
          type="button"
          className="text-pure-white text-[12px] font-medium leading-[14px] flex items-center gap-[4px]"
          onClick={() => navigate(`/scene/${subscription.sceneId}/preview`)}
        >
          이어보기
          <ArrowRightSmall size={14} />
        </button>
      </div>
    </div>
  );
};
